import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
	AnimationContainer,
	AnimationImageAsset,
	AnimationImageElement,
	AnimationPositionElement,
	useAnimationContainerTimeline,
} from '../AnimationCommon';
import Functions from '@client/style/Functions';
import { Breakpoints, presetColors } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import { AnimationElementTreetop } from '../AnimationElementTreetop';
import Breaker from '@client/modules/Dividers/Breaker';
import { MorphSVGPlugin, MotionPathPlugin } from 'gsap/all';
import { gsap } from 'gsap';
import { Power1 } from 'gsap';
import SovBgDesktop from '@client/assets/svgs/animations/sov-bg-desktop.svg';
import SovBgMobile from '@client/assets/svgs/animations/sov-bg-mobile.svg';
import SleepSvg from '@client/assets/svgs/animations/sleep.svg';
import { useSurroundingColors } from '@client/modules/shared/UseSurroundingColors';

const StyledAnimationContainer = styled(AnimationContainer)``;

const BackgroundContainer = styled.div`
	width: 101vw;
	svg {
		width: 100%;
		height: auto;
		fill: ${presetColors.evergreen};
	}
`;

const MobileContainer = styled.span`
	display: block;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: none;
	}
`;
const DesktopContainer = styled.span`
	display: none;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: block;
	}
`;
const ColorWrapper = styled.div`
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: -1;
	position: absolute;
`;

const TopColor = styled.div<{ bgColor?: string }>`
	top: 0;
	left: 0;
	width: 100%;
	height: 50%;
	z-index: -1;
	background-color: ${props => props.bgColor || ''};
	position: absolute;
	${Functions.breakpoint(Breakpoints.mobile)} {
		height: 40%;
	}
	${Functions.breakpoint(Breakpoints.ultra)} {
		height: 50%;
	}
`;

const BottomColor = styled.div<{ bgColor?: string }>`
	bottom: 0;
	left: 0;
	width: 100%;
	height: 50%;
	z-index: -1;
	background-color: ${props => props.bgColor || ''};
	position: absolute;

	${Functions.breakpoint(Breakpoints.mobile)} {
		/* top: 98%; */
		height: 60%;
	}
	${Functions.breakpoint(Breakpoints.ultra)} {
		height: 50%;
	}
`;

const StyledBreaker = styled(Breaker)<{ svgColor?: string }>`
	path {
		fill: ${props => props.svgColor || ''};
	}
`;

const SleepSvgContainer = styled.div`
	width: 60vw;
	svg {
		width: 100%;
		height: auto;
	}
`;

const SleepingZ = styled.div`
	color: ${presetColors.dust};
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	${TextStyle.size('p5', 'p1')}
	position: absolute;
	opacity: 0;
`;

const Tent = () => {
	const image = new AnimationImageAsset('https://www.datocms-assets.com/80292/1686837247-telt.png', {
		width: 1600,
	});
	return (
		<AnimationPositionElement
			position={{
				anchorY: 'bottom',
				width: '90vw',
				x: '-36%',
				y: '-115%',
				breakpoints: { mobile: { width: '50vw', y: '-43%' }, ultra: { x: '-38%' } },
			}}
		>
			<AnimationImageElement image={image} />
		</AnimationPositionElement>
	);
};
const Can = () => {
	const image = new AnimationImageAsset(' https://www.datocms-assets.com/80292/1686837378-can.png', {
		tiny: 400,
		mobile: 600,
	});

	return (
		<>
			<DesktopContainer>
				<AnimationPositionElement
					position={{ anchorY: 'bottom', width: '17vw', anchor: 'right', x: '-60%', y: '-61%' }}
				>
					<AnimationImageElement image={image} />
				</AnimationPositionElement>
				<AnimationPositionElement
					position={{ anchorY: 'bottom', width: '17vw', anchor: 'right', x: '-128.5%', y: '-55%' }}
				>
					<AnimationImageElement image={image} />
				</AnimationPositionElement>
			</DesktopContainer>
			<AnimationPositionElement
				position={{
					anchorY: 'bottom',
					width: '45vw',
					x: '10%',
					anchor: 'right',
					y: '-40%',
					breakpoints: { mobile: { width: '17vw', x: '-69%' } },
				}}
			>
				<AnimationImageElement image={image} />
			</AnimationPositionElement>
		</>
	);
};

const Heart = (props: { variant: 1 | 2 }) => {
	const heart1Ref = React.useRef<HTMLImageElement>(null);
	const heart2Ref = React.useRef<HTMLImageElement>(null);

	const heart1 = new AnimationImageAsset('https://www.datocms-assets.com/80292/1679307288-hjerte-1.png', {
		tiny: 100,
		mobile: 300,
	});
	const heart2 = new AnimationImageAsset('https://www.datocms-assets.com/80292/1679307291-hjerte-2.png', {
		tiny: 205,
		mobile: 300,
	});
	const containerTimeline = useAnimationContainerTimeline();

	useEffect(() => {
		const tween = gsap.to('.heart', {
			stagger: 0.2,
			scale: 1.04,
			duration: 0.4,
			yoyo: true,
			repeat: -1,
			ease: 'back.in(1, 0.3)',
		});
		containerTimeline.add(tween, 0);
		return () => {
			tween.kill();
		};
	}, []);

	return (
		<>
			{props.variant === 1 && (
				<AnimationImageElement image={heart1} className={'heart'} ref={heart1Ref}></AnimationImageElement>
			)}
			{props.variant === 2 && (
				<AnimationImageElement image={heart2} className={'heart'} ref={heart2Ref}></AnimationImageElement>
			)}
		</>
	);
};

const SleepingZs = () => {
	const sleepingZ1 = React.useRef<HTMLDivElement>(null);
	const sleepingZ2 = React.useRef<HTMLDivElement>(null);
	const sleepingZ3 = React.useRef<HTMLDivElement>(null);
	const pathRef = React.useRef<SVGSVGElement>(null);

	const containerTimeline = useAnimationContainerTimeline();

	React.useEffect(() => {
		gsap.registerPlugin(MorphSVGPlugin);
		gsap.registerPlugin(MotionPathPlugin);
		const myPath = pathRef.current?.querySelector<SVGPathElement>('.pathClass');
		if (!myPath) return;
		const tl = gsap.timeline({ repeat: -1 });

		tl.to([sleepingZ1.current, sleepingZ2.current, sleepingZ3.current], {
			duration: 3,
			ease: 'power1.inOut',
			delay: i => [0, 1, 2][i],
			motionPath: {
				path: myPath,
				align: myPath,
				//autoRotate: true,
				alignOrigin: [0.5, 0.5],
			},
		});
		tl.fromTo(
			[sleepingZ1.current, sleepingZ2.current, sleepingZ3.current],
			{
				opacity: 0,
			},
			{
				opacity: 1,
				duration: 0.5,
				delay: i => [0, 1, 2][i],
				ease: Power1.easeIn,
			},
			0
		);
		tl.fromTo(
			[sleepingZ1.current, sleepingZ2.current, sleepingZ3.current],
			{
				opacity: 1,
			},
			{
				opacity: 0,
				duration: 0.5,
				delay: i => [0, 1, 2][i],
				ease: Power1.easeOut,
			},
			'+2.5'
		);
		containerTimeline.add(tl, 0);
		gsap.set([sleepingZ1.current, sleepingZ2.current, sleepingZ3.current], { clearProps: 'all' });
		// containerTimeline.repeat(-1);
		return () => {
			tl.kill();
		};
	}, []);

	return (
		<>
			<AnimationPositionElement
				position={{ x: '38%', y: '25%', breakpoints: { mobile: { x: '10%', y: '-5%' }, ultra: { x: '8%', y: '-2%' } } }}
			>
				<SleepSvgContainer>
					<SleepSvg ref={pathRef} />
				</SleepSvgContainer>
			</AnimationPositionElement>
			<AnimationPositionElement position={{ angle: 10 }}>
				<SleepingZ ref={sleepingZ1}>Z</SleepingZ>
			</AnimationPositionElement>
			<AnimationPositionElement position={{ angle: -10 }}>
				<SleepingZ ref={sleepingZ2}>Z</SleepingZ>
			</AnimationPositionElement>
			<AnimationPositionElement position={{ angle: 15 }}>
				<SleepingZ ref={sleepingZ3}>Z</SleepingZ>
			</AnimationPositionElement>
		</>
	);
};

export default function IllustrationSovSmukt() {
	const moduleRef = useRef<HTMLDivElement | null>(null);
	const backgroundColors = useSurroundingColors(moduleRef);

	return (
		<StyledAnimationContainer
			height={1175}
			width={715}
			breakpoints={{ mobile: { width: 2600, height: 1350 }, ultra: { width: 4000, height: 2250 } }}
			ref={moduleRef}
		>
			<ColorWrapper>
				<TopColor bgColor={backgroundColors[0]}></TopColor>
				<BottomColor bgColor={presetColors.evergreen}></BottomColor>
			</ColorWrapper>

			<DesktopContainer>
				<AnimationElementTreetop
					variant={7}
					position={{
						flip: true,
						width: '10vw',
						anchorY: 'center',
						y: '-73%',
						x: '-7%',
						breakpoints: {
							mobile: { width: '50vw' },
							laptop: { width: '50vw' },
							desktop: { width: '50vw' },
							largeDesktop: { width: '50vw' },
							ultra: { width: '50vw', x: '-23%', y: '-76%' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={2}
					position={{
						y: '-62%',
						anchor: 'right',
						anchorY: 'bottom',

						x: '-35%',
						width: '55vw',
						breakpoints: {
							laptop: { width: '45vw' },
							desktop: { width: '45vw' },
							largeDesktop: { width: '45vw' },
							ultra: { width: '45vw' },
						},
					}}
				></AnimationElementTreetop>

				<AnimationElementTreetop
					variant={2}
					position={{
						y: '-64%',
						flip: true,
						anchor: 'right',
						anchorY: 'bottom',
						x: '-8%',
						width: '55vw',
						breakpoints: {
							laptop: { width: '45vw' },
							desktop: { width: '45vw' },
							largeDesktop: { width: '45vw' },
							ultra: { width: '45vw' },
						},
					}}
				></AnimationElementTreetop>
			</DesktopContainer>
			<AnimationElementTreetop
				variant={3}
				position={{
					anchor: 'right',
					anchorY: 'bottom',
					width: '110vw',
					x: '35%',
					y: '-90%',
					breakpoints: {
						tiny: { width: '120vw' },
						mobile: { width: '45vw', y: '-77%' },
						laptop: { width: '45vw' },
						desktop: { width: '45vw' },
						largeDesktop: { width: '45vw' },
						ultra: { width: '43vw', y: '-105%' },
					},
				}}
			></AnimationElementTreetop>
			<MobileContainer>
				<AnimationElementTreetop
					variant={6}
					position={{
						flip: true,
						anchorY: 'bottom',
						x: '-50%',
						width: '87vw',
						y: '-171%',
						breakpoints: { tiny: { width: '87vw', y: '-171%' } },
					}}
				></AnimationElementTreetop>
			</MobileContainer>

			<MobileContainer>
				<AnimationPositionElement position={{ anchorY: 'bottom', y: '-48%' }}>
					<BackgroundContainer>
						<SovBgMobile />
					</BackgroundContainer>
				</AnimationPositionElement>
			</MobileContainer>
			<DesktopContainer>
				<AnimationPositionElement position={{ anchorY: 'bottom', y: '-135%' }}>
					<BackgroundContainer>
						<SovBgDesktop />
					</BackgroundContainer>
				</AnimationPositionElement>
			</DesktopContainer>

			<AnimationPositionElement position={{ anchorY: 'bottom', height: '25vw', width: '100vw' }}>
				<div style={{ backgroundColor: presetColors.evergreen, width: '100%', height: '100%' }}></div>
			</AnimationPositionElement>
			<Tent />
			<Can />
			<AnimationPositionElement
				position={{
					anchorY: 'bottom',
					width: '12vw',
					anchor: 'right',
					x: '-227%',
					y: '-620%',
					breakpoints: { mobile: { x: '-1117%', y: '-425%', width: '5vw' }, ultra: { x: '-1115%' } },
				}}
			>
				<Heart variant={1} />
			</AnimationPositionElement>
			<AnimationPositionElement
				position={{
					anchorY: 'bottom',
					width: '16vw',
					anchor: 'right',
					x: '-230%',
					y: '-530%',
					breakpoints: { mobile: { y: '-370%', width: '6vw', x: '-1037' }, ultra: { x: '-1005%' } },
				}}
			>
				<Heart variant={2} />
			</AnimationPositionElement>
			<SleepingZs />
			<AnimationPositionElement position={{ anchorY: 'bottom', width: '100vw' }}>
				<StyledBreaker number={0} svgColor={backgroundColors[1]} />
			</AnimationPositionElement>
		</StyledAnimationContainer>
	);
}
